// @ts-nocheck — verbatim port from xop-edge-staff. See src/crm/README.md.
import { isPowerPointFile, isExcelFile, isCsvFile, isWordFile, type FileLike } from '@crm-src/lib/file-type-utils';

/**
 * Pure file → icon-kind + colour mapping, extracted from FileManagerPage's
 * `getFileIcon`. Kept free of React so the (extension/content-type) branching
 * unit-tests directly; the `<FileTypeIcon>` leaf turns a kind into a lucide
 * glyph. No money / reseller here — wall-neutral.
 *
 * This is the single icon mapping for every CRM file surface — the file
 * manager page, the customer-attachment panel + its viewer dialog, and the
 * email-campaign file selector all flow through it (the per-tree `getFileIcon`
 * duplicates were converged onto it 2026-06-23).
 */

/**
 * The minimal file shape the mapping needs: a `name` + optional `content_type`
 * (for the extension/MIME predicates) plus an optional `is_folder` flag. Both
 * the file-manager `FileItem` (files-api — carries `is_folder`) and the
 * customer-attachment `FileItem` (file-manager-panel — no `is_folder`, so it is
 * simply never a folder) satisfy this, so ONE mapping serves them all.
 */
export type FileIconInput = FileLike & { is_folder?: boolean };
export type FileIconKind =
  | 'folder'
  | 'image'
  | 'video'
  | 'audio'
  | 'presentation'
  | 'spreadsheet'
  | 'word'
  | 'pdf'
  | 'code'
  | 'database'
  | 'archive'
  | 'executable'
  | 'text'
  | 'file';

export interface FileIconStyle {
  kind: FileIconKind;
  color: string;
}

const IMAGE_EXT = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg', 'webp', 'ico'];
const VIDEO_EXT = ['mp4', 'webm', 'ogg', 'avi', 'mov', 'wmv', 'flv', 'mkv'];
const AUDIO_EXT = ['mp3', 'wav', 'ogg', 'aac', 'flac', 'm4a', 'wma'];
const SPREADSHEET_EXT = ['xls', 'xlsx', 'xlsm', 'xlsb', 'csv'];
const CODE_EXT = ['js', 'jsx', 'ts', 'tsx', 'html', 'css', 'scss', 'json', 'xml', 'yaml', 'yml', 'py', 'rb', 'php', 'java', 'c', 'cpp', 'cs', 'go', 'rs', 'swift', 'kt', 'sql'];
const DATABASE_EXT = ['db', 'sqlite', 'sqlite3', 'mdb'];
const ARCHIVE_EXT = ['zip', 'rar', 'tar', 'gz', '7z', 'bz2', 'xz'];
const EXECUTABLE_EXT = ['exe', 'msi', 'dmg', 'pkg', 'deb', 'rpm', 'app'];
const TEXT_EXT = ['txt', 'md', 'log', 'cfg', 'conf', 'ini'];

/**
 * Decide the icon kind + colour for a file. Folders short-circuit; otherwise the
 * content-type prefix or extension picks the kind, matching `getFileIcon`'s
 * order exactly (the first match wins), with a neutral `file` fallback.
 */
export function fileIconStyle(file: FileIconInput): FileIconStyle {
  if (file.is_folder) return { kind: 'folder', color: '#3b82f6' };

  const ext = file.name.split('.').pop()?.toLowerCase();
  const contentType = file.content_type;
  const isExt = (list: string[]) => list.includes(ext || '');

  if (contentType?.startsWith('image/') || isExt(IMAGE_EXT)) return { kind: 'image', color: '#22c55e' };
  if (contentType?.startsWith('video/') || isExt(VIDEO_EXT)) return { kind: 'video', color: '#a855f7' };
  if (contentType?.startsWith('audio/') || isExt(AUDIO_EXT)) return { kind: 'audio', color: '#ec4899' };
  if (isPowerPointFile(file) || ['ppt', 'pptx'].includes(ext || '')) return { kind: 'presentation', color: '#f97316' };
  if (isExcelFile(file) || isCsvFile(file) || isExt(SPREADSHEET_EXT)) return { kind: 'spreadsheet', color: '#16a34a' };
  if (isWordFile(file) || ['doc', 'docx'].includes(ext || '')) return { kind: 'word', color: '#2563eb' };
  if (contentType === 'application/pdf' || ext === 'pdf') return { kind: 'pdf', color: '#ef4444' };
  if (isExt(CODE_EXT)) return { kind: 'code', color: '#6366f1' };
  if (isExt(DATABASE_EXT)) return { kind: 'database', color: '#14b8a6' };
  if (isExt(ARCHIVE_EXT)) return { kind: 'archive', color: '#ca8a04' };
  if (isExt(EXECUTABLE_EXT)) return { kind: 'executable', color: '#dc2626' };
  if (contentType?.startsWith('text/') || isExt(TEXT_EXT)) return { kind: 'text', color: '#4b5563' };

  return { kind: 'file', color: '#6b7280' };
}
